import { TourAnchor } from "training/anchors";
import {
  directPayEnabled,
  hasEstimates,
  hasMultipleAccounts,
  not,
} from "training/engine/conditions";
import { ITourStep, TourModule } from "training/types";

import { MODULES } from "./modules";

/** "Module 1: Navigating the Estimates page, about 5 minutes" for each module, from MODULES (never drifts). */
export const moduleOutline = (): string[] =>
  (Object.keys(MODULES) as unknown as TourModule[]).map((n) => {
    const { title, minutes } = MODULES[n];
    return minutes
      ? `Module ${n}: ${title}, about ${minutes} minutes`
      : `Module ${n}: ${title}`;
  });

/**
 * CLE-3015 Module 1: Navigating the Estimates page. Text is John's (review 1, 2026-10-23) where he gave
 * wording; the rest is the curriculum's (curriculum-v2, 2026-10-21). Two curriculum rows point at two elements
 * each (1.7 the date and status filters, 1.9 View and the "..." menu); each is split into two steps, so each
 * element gets its own highlight. The row steps (1.8, 1.9) need a first row; on a hospital with no estimates
 * one card (`m1.08n-row-controls-card`, not curriculum text) replaces them.
 *
 * Reorder (review 1): 1.5, the status definitions, runs after the two filters, so the page reads from the
 * monthly collections to the table, then each filter, and only then what each status means. Its id is
 * unchanged.
 */
export const MODULE_1: ITourStep[] = [
  {
    // The welcome card. The id predates it (it was the sign-in card); ids are never renumbered.
    id: "m1.00-login-card",
    module: 1,
    mode: "card",
    route: "estimates",
    text: [
      "Welcome to your Clear Health Certification course! This will run you through the basics of the platform so you know your way around ahead of more formal training. A couple of quick housekeeping items before we begin:",
      "• Bookmark or favorite this page so it is easily accessible.",
      "• support@clearhealthinc.com is the most direct way to get timely feedback to any question or issue you are having.",
      '• Accounts that go unused for 90 days are disabled automatically. Users will receive a warning email about inactivity first. If you ever see "Your access has been disabled", email support@clearhealthinc.com.',
      "Here is what the course covers:",
      ...moduleOutline(),
    ].join("\n"),
  },
  {
    id: "m1.01-estimates-tab",
    module: 1,
    anchor: TourAnchor.EstimatesTab,
    label: "Estimates",
    mode: "look",
    route: "estimates",
    text: "This is your home page and we have tried to keep this as simple as possible — everything you need to do in the process of creating patient estimates lives here in this Estimates tab.",
  },
  {
    id: "m1.02-facility-switcher",
    module: 1,
    anchor: TourAnchor.FacilitySwitcher,
    mode: "look",
    route: "estimates",
    when: hasMultipleAccounts,
    text: "If you work at more than one facility, pick it here. The red number is how many estimates there need attention. The toggle sets which one opens when you log in.",
  },
  {
    id: "m1.03-monthly-collections",
    module: 1,
    anchor: TourAnchor.MonthlyCollections,
    label: "Monthly collections",
    mode: "look",
    route: "estimates",
    text: "This area will give you an instant view into how well the facility is tracking towards monthly collections. Expected is calculated from estimates sent for patients this month. Collected is what has actually been transacted through the platform, minus refunds.",
  },
  {
    // The anchor is on the table's header row, not the whole table: driver.js scrolls a highlighted element
    // that is not fully in view into view — centred, or its top at the top of the viewport when it is taller
    // than the viewport — and the whole table is that tall, so the page jumped away from the collections card
    // (review 1). The header row sits right under the filters and is already in view.
    id: "m1.04-estimates-table",
    module: 1,
    anchor: TourAnchor.EstimatesTable,
    mode: "look",
    route: "estimates",
    text: "This will act as a repository for every estimate your team has created, sorting by the newest first.",
  },
  {
    id: "m1.06-search-box",
    module: 1,
    anchor: TourAnchor.SearchBox,
    label: "Search by patient name, payer, or account number",
    mode: "look",
    route: "estimates",
    text: "Find a patient fast. It also matches the account number from your system and the insurance member ID.",
  },
  {
    id: "m1.07-date-filter",
    module: 1,
    anchor: TourAnchor.DateFilter,
    label: "Date of service:",
    mode: "look",
    route: "estimates",
    text: "Use the Date of Service Filter to quickly look at patients within a specific Date of Service Range. Another popular use is to use this filter to see who is coming in today and has not paid. Set the date to Today and the status to Sent. That is the list you work from each morning.",
  },
  {
    id: "m1.07b-status-filter",
    module: 1,
    anchor: TourAnchor.StatusFilter,
    label: "Filter by:",
    mode: "look",
    route: "estimates",
    text: "Use this to filter based on patient statuses. A common best practice is to use the Date of Service set to Today and change the status to Paid. This gives you the end-of-day list of who has paid.",
  },
  {
    // Moved here from after 1.4 (review 1, see the header).
    id: "m1.05-status-column",
    module: 1,
    anchor: TourAnchor.StatusColumn,
    title: "Status Definitions",
    mode: "look",
    route: "estimates",
    // One status per line; the popover renders "Label: explanation" lines as a tight list.
    text: [
      "The status section will allow your team at a glance to know if a patient has already paid their estimate or it needs further action.",
      "Sent: texted, not paid.",
      "Partial: part paid, or on a plan.",
      "Paid: paid in full.",
      "Action Needed: a draft, or insurance came back inactive.",
      "Expired: the visit passed unpaid.",
      "You may also see No Balance Due, Paid via Cash, Refunded and Canceled.",
    ].join("\n"),
  },
  {
    id: "m1.08-collect-payment",
    module: 1,
    anchor: TourAnchor.CollectPaymentFirstRow,
    label: "Collect Payment",
    mode: "look",
    route: "estimates",
    when: hasEstimates,
    text: 'This is used for when a patient calls in about their estimate or is at the front desk and wants to pay in person. Simply search their name to pull up their estimate and hit "Collect Payment". This will open the payment page and allow you to take payment.',
  },
  {
    id: "m1.09-view",
    module: 1,
    anchor: TourAnchor.ViewFirstRow,
    label: "View",
    mode: "look",
    route: "estimates",
    when: hasEstimates,
    text: "Clicking View will open up the actual estimate itself, giving staff a complete picture of the patient demographics, insurance, etc. that was input at the creation of the estimate.",
  },
  {
    id: "m1.09b-row-menu",
    module: 1,
    anchor: TourAnchor.RowMenuFirstRow,
    label: "Estimate actions",
    mode: "look",
    route: "estimates",
    when: hasEstimates,
    text: "The 3 little dots (called an ellipsis) bring up the quick action menu. Staff can View the Estimate, Resend, Duplicate, Copy the Link, or Cancel the estimate directly from these quick actions. We come back to these in Module 3.",
  },
  {
    // Not curriculum text — Module 1 runs before the trainee has created anything, and a hospital with no
    // estimates has no first row: one card stands in for 1.8 and 1.9.
    id: "m1.08n-row-controls-card",
    module: 1,
    mode: "card",
    route: "estimates",
    when: not(hasEstimates),
    text: 'Once estimates exist, each row has three controls. Collect Payment, on Sent estimates only, opens the patient\'s payment page for you when they are at the desk. View opens the full estimate. The "…" menu has View, Resend, Duplicate, Copy link and Cancel. You will see them on your own estimate after Module 2.',
  },
  {
    id: "m1.10-direct-pay",
    module: 1,
    anchor: TourAnchor.DirectPay,
    label: "Direct Pay",
    mode: "look",
    route: "estimates",
    when: directPayEnabled,
    text: [
      "This is a payment link that can be used to collect payment for an amount not tied directly to an old estimate, such as an old balance. Module 4 will cover this in more detail.",
      "Note: This can also be embedded into your facility's website to allow patients self-service payment options.",
    ].join("\n"),
  },
  {
    id: "m1.11-new-estimate",
    module: 1,
    anchor: TourAnchor.NewEstimate,
    label: "New Estimate",
    mode: "look",
    route: "estimates",
    text: "By clicking New Estimate, staff will be taken to the next screen and be prompted to enter the necessary information in for the estimate to be accurately created.",
  },
  {
    // The id predates the card's "Best practices!" heading (it was the daily-email card).
    id: "m1.12-daily-email-card",
    module: 1,
    title: "Best practices!",
    mode: "card",
    route: "estimates",
    text: [
      "• Each weekday at 4:00 AM Central, Clear Health emails a list of today's patients who still owe. It goes to the addresses your hospital gave us. Ask support if you should be on it. Weekend appointments are not emailed.",
      "• Stay logged in for your shift. Each sign-in needs a new security code, so one login a day saves you doing it twice.",
    ].join("\n"),
  },
];
